import type { FC } from 'react';

import CheckmarkCircle from '@crehana/compass.icons.fill/CheckmarkCircle';
import Clock from '@crehana/compass.icons.outline/Clock';
import { ProgressBar } from '@crehana/compass.ui';

import { TrackCompletionRequirementTypeEnum } from '@/generated/graphql/types';

import useTeamCoursesTranslation from '../../hooks/useTeamCoursesTranslation/useTeamCoursesTranslation';
import type {
  TTrackCompletionCriteriaProps,
  TTrackCompletionRequirement,
} from './types';

const PERCENTAGE_SCALE = 100;

const { ContentCompletion, FinalEvaluationApproved } =
  TrackCompletionRequirementTypeEnum;

const percentProgress = (value: number) => {
  const bounded = Math.min(PERCENTAGE_SCALE, Math.max(0, value));

  return { bar: bounded, label: `${bounded}%` };
};

/**
 * Renders the completion requirements the organization configured for a track.
 *
 * Backend answers only the ACTIVE requirements, already in the order of the
 * admin tab, and every one of them is compared with ">=" against its own
 * minimum: there is no operator to render, nothing to sort and nothing to
 * filter out here. It carries no retry endpoint either, so the CTA points at
 * the first course with a pending quiz.
 */
export const TrackCompletionCriteria: FC<TTrackCompletionCriteriaProps> = ({
  requirements,
  requiredCourses,
  projectCoursesCount = 0,
  quizCoursesCount = 0,
  pendingQuizCourseUrl,
  isTrackClosed,
}) => {
  const { t } = useTeamCoursesTranslation();

  // Empty means the track has no rule configured; the caller falls back to the
  // legacy summary rows.
  if (!requirements.length) {
    return null;
  }

  // The value field always shows current_value, the number the collaborator
  // actually reached, normalized by backend to the same 0-100 scale as
  // threshold. No value means nothing reached yet, which is 0 — never a course
  // count and never the threshold, which is the goal and not the achievement.
  // Exception: a requirement backend satisfies trivially (nothing to grade)
  // arrives satisfied with no current_value at all. Rendering 0 there puts an
  // empty bar next to the green checkmark, two contradictory signals in one
  // row, so the bar is filled. A satisfied requirement that DOES carry a value
  // still shows that value, never the threshold.
  const requirementProgress = ({
    current_value,
    satisfied,
  }: TTrackCompletionRequirement) => {
    if (satisfied && current_value == undefined) {
      return percentProgress(PERCENTAGE_SCALE);
    }

    return percentProgress(current_value ?? 0);
  };

  const requirementLabel = (requirement: TTrackCompletionRequirement) => {
    if (requirement.requirement_type === ContentCompletion) {
      if (requirement.threshold != undefined) {
        return t('LEARN_TEAM_RULE_CONTENT_COMPLETION_PERCENT', {
          interpolation: { threshold: requirement.threshold },
        });
      }

      if (requiredCourses > 0) {
        return t('LEARN_TEAM_RULE_CONTENT_COMPLETION', {
          interpolation: { count: requiredCourses },
        });
      }

      return t('LEARN_TEAM_RULE_CONTENT_COMPLETION_NO_THRESHOLD');
    }

    if (requirement.requirement_type === FinalEvaluationApproved) {
      if (requirement.threshold == undefined) {
        return t('LEARN_TEAM_RULE_FINAL_EVALUATION_NO_THRESHOLD', {
          interpolation: { count: quizCoursesCount },
        });
      }

      return t('LEARN_TEAM_RULE_FINAL_EVALUATION', {
        interpolation: {
          count: quizCoursesCount,
          threshold: requirement.threshold,
        },
      });
    }

    return t('LEARN_TEAM_RULE_FINAL_PROJECT', {
      interpolation: { count: projectCoursesCount },
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-1.2">
      {requirements.map(requirement => {
        const progress = requirementProgress(requirement);
        const shouldRenderBestAttempt =
          requirement.requirement_type === FinalEvaluationApproved &&
          !requirement.satisfied &&
          requirement.current_value != undefined &&
          requirement.threshold != undefined;

        return (
          <div
            key={requirement.requirement_type}
            className="tw-flex tw-flex-col tw-gap-0.8 tw-pb-1.2 tw-border-b tw-border-solid tw-border-neutral-light-300 last:tw-border-b-0"
          >
            <div className="tw-flex tw-items-center tw-gap-0.8 tw-font-body5 tw-text-neutral-light-800 dark:tw-text-neutral-light-100">
              {requirement.satisfied ? (
                <CheckmarkCircle
                  size={20}
                  className="tw-cds-text-success tw-shrink-0"
                />
              ) : (
                <Clock size={20} className="tw-cds-text-progress tw-shrink-0" />
              )}
              <span>{requirementLabel(requirement)}</span>
            </div>

            <div className="tw-flex tw-items-center tw-gap-0.8 tw-font-body5 tw-text-neutral-light-800 dark:tw-text-neutral-light-100">
              <ProgressBar
                size="lg"
                progress={progress.bar}
                className="tw-rounded-0.8 tw-mr-0.8 tw-p-0.4"
                detailBackground={{
                  background:
                    'tw-bg-neutral-light-400 dark:tw-bg-neutral-dark-700',
                  progressBar:
                    'tw-rounded-0.8 tw-bg-primary-light-400 dark:tw-bg-primary-light-300',
                }}
              />
              {progress.label}
            </div>

            {shouldRenderBestAttempt && (
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-1.6 tw-p-1.2 tw-rounded-0.8 tw-bg-neutral-light-200 dark:tw-bg-elevation-dark-06">
                <p className="tw-font-body5 tw-text-neutral-light-800 dark:tw-text-neutral-light-100">
                  {t('LEARN_TEAM_RULE_BEST_ATTEMPT', {
                    interpolation: {
                      current: requirement.current_value,
                      threshold: requirement.threshold,
                    },
                  })}
                </p>
                {pendingQuizCourseUrl && !isTrackClosed && (
                  <a
                    className="tw-btn-primary-s tw-flex tw-items-center tw-justify-center tw-shrink-0"
                    href={pendingQuizCourseUrl}
                  >
                    {t('LEARN_TEAM_RULE_GO_TO_EVALUATION')}
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
